import { useState, useMemo } from 'react';

const MIN_SEARCH_LENGTH = 1;

interface IUseCollectionSearchResult<T> {
  query: string,
  isSearchActive: boolean,
  setQuery: (query: string) => void,
  filteredCollection: T[],
}

const stripSpecial = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();

const matchesQuery = (text: string, query: string): boolean => {
  const strippedText = stripSpecial(text);
  const parts = stripSpecial(query).split(' ').filter((p) => p.length >= MIN_SEARCH_LENGTH);

  if (!parts.length) return false;
  return parts.every((part) => strippedText.includes(part));
};

const useCollectionSearch = <T>(
  collection: T[],
  getText: (item: T) => string,
): IUseCollectionSearchResult<T> => {
  const [query, setQuery] = useState('');

  const isSearchActive = query.trim().length >= MIN_SEARCH_LENGTH;

  const filteredCollection = useMemo<T[]>(() => {
    if (!isSearchActive) return collection;
    const strippedQuery = stripSpecial(query);
    if (!strippedQuery) return [];
    return collection.filter((item) => matchesQuery(getText(item), query));
  }, [query, isSearchActive, collection, getText]);

  return { query, isSearchActive, setQuery, filteredCollection };
};

export default useCollectionSearch;
